import React, { Component } from 'react'
import classNames from 'classnames'
import Scrollbars from 'react-custom-scrollbars'
import PropTypes from 'prop-types'

import AbsolutePopover from 'app/domains/common/AbsolutePopover'
import { fuzzySearch } from 'helpers/fuzzySearch'
import { getJoinedUserFullName, getUserFullName, getUserInitials } from 'helpers/getUserUIData'
import UserAvatarWithStats from 'app/components/UserAvatarWithStats'
import InputTrigger from './InputTrigger'

import './styles.scss'
export const bc = 'mention-text-area'

class MentionTextArea extends Component {
  state = {
    top: null,
    left: null,
    showTooltip: false,
    startPosition: null,
    filter: null,
    currentSelection: 0,
  }

  resetState = () => {
    this.setState({
      showTooltip: false,
      left: null,
      top: null,
      filter: null,
      startPosition: null,
    })
  }

  handleToggleTooltip = metaInformation => {
    const { hookType, cursor } = metaInformation
    if (hookType === 'start') {
      this.setState({
        showTooltip: true,
        left: cursor.left,
        top: cursor.top + (cursor.height || 24),
        startPosition: cursor.selectionStart,
      })
    }

    if (hookType === 'cancel') {
      this.resetState()
    }
  }

  handleOnType = metaInformation => {
    this.state.showTooltip &&
      this.setState({
        filter: metaInformation.text,
      })
  }

  handleOnKeyDown = event => {
    const { which } = event
    const { showTooltip, currentSelection } = this.state
    const users = this.getFilteredUser()
    // 40 = 'arrow down'
    if (which === 40 && showTooltip) {
      event.preventDefault()
      this.setState({
        currentSelection: (currentSelection + 1) % users.size,
      })
    }
    // 38 = 'arrow up'
    if (which === 38 && showTooltip) {
      event.preventDefault()
      this.setState({
        currentSelection: currentSelection > 0 ? currentSelection - 1 : 0,
      })
    }
    // 13 = 'enter'
    if (which === 13 && showTooltip) {
      event.preventDefault()
      const user = users.get(currentSelection)
      this.insertUser(user)
    }
    // 27 = 'Esc'
    if (which === 27 && showTooltip) {
      this.onEndHandler && this.onEndHandler()
      this.resetState()
    }
  }

  insertUser = user => {
    const { value, onChange, setMentionUser } = this.props
    const { filter } = this.state
    const { startPosition } = this.state
    const { firstName, lastName, email } = user.toJS()
    const userName = getJoinedUserFullName(firstName, lastName, email)

    const newText = `${value.slice(0, startPosition)}${userName}${value.slice(startPosition + (filter || '').length)}`

    onChange(newText)
    setMentionUser(user)
    this.onEndHandler && this.onEndHandler()
    this.resetState()
  }

  renderUser = (user, index) => {
    const { firstName, lastName, avatar, email } = user.toJS()
    const initials = getUserInitials(firstName, lastName, email)
    const fullName = getUserFullName(firstName, lastName, email)

    return (
      <div
        className={classNames(`${bc}__user`, {
          selected: index === this.state.currentSelection,
        })}
        onClick={() => this.insertUser(user)}
        key={'user' + index}
      >
        <div className={`${bc}__avatar`}>
          <UserAvatarWithStats
            isReactionPopupTagged
            isReactionPopup
            avatar={avatar}
            initials={initials}
            circleProps={{
              cx: 20,
              cy: 20,
              strokeWidth: 2,
              r: 19,
            }}
          />
        </div>
        <div className={`${bc}__fullname`}>{fullName}</div>
      </div>
    )
  }

  getFilteredUser = () => {
    const { filter } = this.state
    const { users, identity } = this.props
    if (!filter) {
      return users
    }

    return users.filter(item => {
      if (item.get('id') === identity.get('id')) {
        return false
      }
      const firstName = item.get('firstName')
      const lastName = item.get('lastName')
      const email = item.get('email')
      return (
        (!firstName && !lastName && fuzzySearch(filter, email)) ||
        (firstName && fuzzySearch(filter, firstName)) ||
        (lastName && fuzzySearch(filter, lastName))
      )
    })
  }

  renderUsersTooltip = () => {
    const { top, left, showTooltip } = this.state
    if (!this.props.users || typeof top !== 'number' || typeof left !== 'number') {
      return null
    }

    return (
      <AbsolutePopover
        top={top}
        left={left}
        isOpen={showTooltip}
        tipSize={7}
        preferPlace="below"
        body={
          <div className={`${bc}__tooltip`}>
            <Scrollbars
              autoHeight
              autoHeightMax={252}
              renderThumbVertical={props => <div {...props} className="scrollbar-thumb-vertical" />}
            >
              {this.getFilteredUser().map(this.renderUser)}
            </Scrollbars>
          </div>
        }
      />
    )
  }

  render() {
    const { id, name, value, placeholder, className, onChange, onBlur, onFocus, onKeyDown } = this.props
    // charCode = 64 is @
    return (
      <div className={bc} onKeyDown={this.handleOnKeyDown}>
        <InputTrigger
          trigger={{
            keyCode: 50,
            shiftKey: true,
            charCode: 64,
          }}
          mobileTrigger={{ keyCode: 64 }}
          onStart={this.handleToggleTooltip}
          onCancel={this.handleToggleTooltip}
          onType={this.handleOnType}
          endTrigger={endHandler => {
            this.onEndHandler = endHandler
          }}
        >
          <textarea
            placeholder={placeholder}
            className={className}
            name={name}
            id={id}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
          />
        </InputTrigger>
        {this.renderUsersTooltip()}
      </div>
    )
  }
}

MentionTextArea.propTypes = {
  value: PropTypes.string,
  name: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  setMentionUser: PropTypes.func.isRequired,
  identity: PropTypes.object.isRequired,
}

export default MentionTextArea
